import React from "react";
import { useLocation, Navigate } from "react-router-dom";
import {
  MapPin,
  Phone,
  Clock,
  Star,
  Sparkles,
  Check,
  Navigation,
  ExternalLink,
} from "lucide-react";
import { carwashes, reviewsByLocation } from "../data/carwashes";

const Location = () => {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\//, "").split("/")[0];
  const data = carwashes[slug];

  if (!data) return <Navigate to="/" replace />;

  const reviews = reviewsByLocation[slug] || [];

  const handleDirections = (e) => {
    e.preventDefault();
    const query = encodeURIComponent(data.address);
    // Universal Google Maps URL — works in all browsers
    const url = `https://www.google.com/maps/search/${query}`;
    window.open(url, "_blank", "noopener");
  };

  return (
    <main className="relative z-10 pt-24" data-testid={`location-${slug}`}>
      {/* HERO */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 sm:pt-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div className="reveal">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-cyan-400/10 border border-cyan-400/20 text-cyan-300 text-xs font-semibold uppercase tracking-[0.18em] mb-6 font-body">
              <Sparkles className="w-3.5 h-3.5" />
              {data.brand}
            </div>
            <h1
              data-testid="location-title"
              className="font-display font-black text-5xl sm:text-6xl tracking-tighter leading-[0.95] text-white"
            >
              {data.name.split(" ").slice(0, 1).join(" ")}
              <br />
              <span className={`bg-gradient-to-r ${data.accent} bg-clip-text text-transparent`}>
                {data.name.split(" ").slice(1).join(" ")}
              </span>
            </h1>
            <p className="text-slate-300 mt-6 text-lg leading-relaxed font-body max-w-xl">
              {data.description}
            </p>

            <div className="flex flex-wrap gap-3 mt-8">
              <a
                href={data.mapsLink}
                onClick={handleDirections}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-directions"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-cyan-400 text-slate-950 font-display font-bold text-sm hover:bg-cyan-300 transition-all shadow-[0_8px_32px_rgba(0,229,255,0.3)] hover:shadow-[0_12px_40px_rgba(0,229,255,0.5)]"
              >
                <Navigation className="w-4 h-4" strokeWidth={2.5} />
                Indicazioni stradali
              </a>
              <a
                href={`tel:${data.phones[0].number.replace(/\s/g, "")}`}
                data-testid="cta-call"
                className="inline-flex flex-col items-start gap-0 px-5 py-2.5 rounded-2xl bg-white/5 border border-white/10 text-white hover:bg-white/10 transition-all"
              >
                <span className="text-[10px] uppercase tracking-[0.18em] font-body font-bold text-cyan-300">
                  Sede di {data.shortAddress.split(" ")[0]} · {data.phones[0].type}
                </span>
                <span className="flex items-center gap-2 font-display font-bold text-sm">
                  <Phone className="w-4 h-4" strokeWidth={2.5} />
                  {data.phones[0].number}
                </span>
              </a>
            </div>

            <div className="grid grid-cols-3 gap-4 mt-10">
              <InfoChip icon={<Clock className="w-4 h-4" />} label="Orari" value={data.hoursShort} />
              {data.rating ? (
                <InfoChip
                  icon={<Star className="w-4 h-4 fill-amber-400 stroke-amber-400" />}
                  label="Valutazione Google"
                  value={`${data.rating} / 5`}
                />
              ) : (
                <InfoChip
                  icon={<Sparkles className="w-4 h-4" />}
                  label="Tipologia"
                  value="Self-service"
                />
              )}
              {data.reviewsCount ? (
                <InfoChip
                  icon={<Sparkles className="w-4 h-4" />}
                  label="Recensioni"
                  value={`${data.reviewsCount}`}
                />
              ) : (
                <InfoChip
                  icon={<Sparkles className="w-4 h-4" />}
                  label="Pagamento"
                  value="Contanti · POS · Card"
                />
              )}
            </div>
          </div>

          <div
            className="relative aspect-[4/5] rounded-3xl overflow-hidden border border-white/10 reveal glow-cyan"
            style={{ animationDelay: "0.15s" }}
            data-testid="hero-image-card"
          >
            <img
              src={data.heroImage}
              alt={data.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <div className="glass rounded-2xl p-4 flex items-center gap-3">
                <MapPin className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                <span className="text-sm font-body text-white">{data.address}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES / PRICES */}
      {data.services && data.services.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-28" data-testid="services-section">
          <SectionHeader
            eyebrow="Programmi & Listino"
            title="Lavaggi su misura per ogni veicolo"
            subtitle="Programmi disponibili in sede."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
            {data.services.map((s, i) => (
              <div
                key={i}
                data-testid={`service-${i}`}
                className="glass rounded-2xl p-6 group hover:border-cyan-400/40 transition-all card-lift"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-display font-bold text-lg text-white">{s.name}</h3>
                    <p className="text-sm text-slate-400 mt-1 font-body leading-relaxed">{s.desc}</p>
                  </div>
                  <span className="font-display font-black text-cyan-400 text-xl whitespace-nowrap">
                    {s.price}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* VERIFIED FACTS */}
      {data.verifiedFacts && data.verifiedFacts.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-28" data-testid="facts-section">
          <SectionHeader
            eyebrow="In sintesi"
            title="Caratteristiche principali"
            subtitle="Informazioni verificate sulla sede."
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-12">
            {data.verifiedFacts.map((f, i) => (
              <div
                key={i}
                data-testid={`fact-${i}`}
                className="flex items-center gap-3 glass rounded-xl px-5 py-4"
              >
                <span className="w-7 h-7 rounded-full bg-cyan-400/15 border border-cyan-400/30 grid place-items-center flex-shrink-0">
                  <Check className="w-3.5 h-3.5 text-cyan-300" strokeWidth={3} />
                </span>
                <span className="text-sm font-body text-slate-200">{f}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* FEATURES */}
      {data.features && data.features.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-28">
          <SectionHeader
            eyebrow="Cosa trovi in sede"
            title="Dotazioni e plus della sede"
            subtitle="Tutto quello che serve per uscire con un'auto come nuova, a qualsiasi ora."
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-12">
            {data.features.map((f, i) => (
              <div
                key={i}
                data-testid={`feature-${i}`}
                className="flex items-center gap-3 glass rounded-xl px-5 py-4"
              >
                <span className="w-7 h-7 rounded-full bg-cyan-400/15 border border-cyan-400/30 grid place-items-center flex-shrink-0">
                  <Check className="w-3.5 h-3.5 text-cyan-300" strokeWidth={3} />
                </span>
                <span className="text-sm font-body text-slate-200">{f}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* GALLERY */}
      {data.gallery && data.gallery.length > 0 && (
        <section
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-28"
          data-testid="service-gallery"
        >
          <SectionHeader
            eyebrow="Galleria"
            title="La sede in immagini"
            subtitle="Foto reali scattate in loco."
          />
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
            {data.gallery.map((src, i) => (
              <div
                key={i}
                data-testid={`gallery-img-${i}`}
                className="relative overflow-hidden rounded-2xl border border-white/10 group aspect-square"
              >
                <img
                  src={src}
                  alt={`Galleria ${data.name} ${i + 1}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* MAP */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-28">
        <SectionHeader
          eyebrow="Come raggiungerci"
          title="Trovaci facilmente"
          subtitle={data.address}
        />
        <div
          data-testid="google-maps-embed"
          className="mt-12 rounded-3xl overflow-hidden border border-white/10 glow-cyan aspect-[16/10] sm:aspect-[21/9]"
        >
          <iframe
            title={`Mappa ${data.name}`}
            src={data.mapsEmbed}
            className="w-full h-full"
            style={{ border: 0, filter: "invert(0.92) hue-rotate(180deg)" }}
            loading="lazy"
            allowFullScreen
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
        <a
          href={data.mapsLink}
          onClick={handleDirections}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="open-in-maps"
          className="inline-flex items-center gap-2 mt-6 text-sm text-cyan-300 hover:text-cyan-200 font-body font-semibold"
        >
          Apri in Google Maps <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </section>

      {/* REVIEWS */}
      {reviews.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-28" data-testid="reviews-section">
          <SectionHeader
            eyebrow="Dicono di noi"
            title={`Le voci dei clienti${data.rating ? ` — ${data.rating}★` : ""}`}
            subtitle={
              data.reviewsCount
                ? `Una selezione di recensioni dai ${data.reviewsCount} feedback Google.`
                : "Una selezione di recensioni dei clienti."
            }
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-12">
            {reviews.map((r, i) => (
              <div
                key={i}
                data-testid={`review-${i}`}
                className="glass rounded-2xl p-6 card-lift"
              >
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: 5 }).map((_, j) => (
                    <Star
                      key={j}
                      className={`w-4 h-4 ${
                        j < r.rating ? "fill-amber-400 stroke-amber-400" : "stroke-slate-600"
                      }`}
                    />
                  ))}
                </div>
                <p className="text-slate-200 font-body leading-relaxed text-sm">&ldquo;{r.text}&rdquo;</p>
                <div className="mt-4 text-xs uppercase tracking-[0.18em] text-cyan-300 font-bold font-body">
                  — {r.name}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-28">
        <div className="relative overflow-hidden rounded-3xl border border-cyan-400/20 glass p-10 sm:p-16 text-center">
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-transparent to-blue-500/10" />
          <div className="relative">
            <h2 className="font-display font-black text-3xl sm:text-5xl tracking-tighter text-white">
              Pronto a far brillare la tua auto?
            </h2>
            <p className="text-slate-300 mt-4 max-w-2xl mx-auto font-body">
              La sede di {data.shortAddress} ti aspetta 24 ore su 24. Passa
              quando vuoi, anche al volo nella pausa pranzo.
            </p>
            <div className="flex flex-wrap gap-3 mt-8 justify-center">
              <a
                href={data.mapsLink}
                onClick={handleDirections}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-bottom-directions"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-cyan-400 text-slate-950 font-display font-bold text-sm hover:bg-cyan-300 transition-all"
              >
                <Navigation className="w-4 h-4" strokeWidth={2.5} />
                Indicazioni
              </a>
              {data.phones.map((p, i) => (
                <a
                  key={i}
                  href={`tel:${p.number.replace(/\s/g, "")}`}
                  data-testid={`cta-bottom-phone-${i}`}
                  className="inline-flex flex-col items-start gap-0 px-5 py-2.5 rounded-2xl bg-white/5 border border-white/10 text-white hover:bg-white/10 transition-all"
                >
                  <span className="text-[10px] uppercase tracking-[0.18em] font-body font-bold text-cyan-300">
                    Sede di {data.shortAddress.split(" ")[0]} · {p.type}
                  </span>
                  <span className="flex items-center gap-2 font-display font-bold text-sm">
                    <Phone className="w-4 h-4" strokeWidth={2.5} /> {p.number}
                  </span>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

const InfoChip = ({ icon, label, value }) => (
  <div className="glass rounded-xl px-4 py-3">
    <div className="flex items-center gap-1.5 text-cyan-300 text-xs uppercase tracking-[0.15em] font-body font-semibold">
      {icon}
      {label}
    </div>
    <div className="font-display font-bold text-white text-base mt-1">{value}</div>
  </div>
);

const SectionHeader = ({ eyebrow, title, subtitle }) => (
  <div className="max-w-3xl">
    <div className="text-xs uppercase tracking-[0.22em] text-cyan-400 font-bold font-body">
      {eyebrow}
    </div>
    <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight text-white mt-3">
      {title}
    </h2>
    {subtitle && (
      <p className="text-slate-400 mt-3 font-body leading-relaxed">{subtitle}</p>
    )}
  </div>
);

export default Location;
